// Hotspot Explorer — 17th interaction type (restored from Dylan's original
// Kula Create catalogue; the LMS port previously shipped 16 + 1 orphan).
//
// Pedagogy: spatial / visual exploration. The learner clicks numbered zones
// on an image to reveal layered detail; exploring every zone unlocks a
// synthesis block + reflection prompt. Grounded in dual coding (Paivio),
// spatial cognition, and self-directed exploration.
//
// Single-row capture on synthesis-submit:
//   { explored: string[], explorationOrder: string[], allExplored: bool, reflection: string }

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { HotspotExplorerSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: HotspotExplorerSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: HotspotExplorerSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}

export function HotspotExplorerSlide({ data, theme, isEditing, onUpdate, onActivityResponse }: Props) {
  if (isEditing) return <HotspotEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return <HotspotStudent data={data} theme={theme} onActivityResponse={onActivityResponse} />;
}

// ---- Student --------------------------------------------------------------

function HotspotStudent({
  data,
  theme,
  onActivityResponse,
}: {
  data: HotspotExplorerSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const [openId, setOpenId] = useState<string | null>(null);
  const [explored, setExplored] = useState<string[]>([]);
  const [reflection, setReflection] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const allExplored = explored.length === data.hotspots.length && data.hotspots.length > 0;

  const openHotspot = useCallback(
    (id: string) => {
      setOpenId(id);
      setExplored((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [],
  );

  const closePanel = useCallback(() => setOpenId(null), []);

  // ESC closes the open panel — mirrors Dylan's §7 a11y constraint.
  useEffect(() => {
    if (!openId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openId, closePanel]);

  // Auto-focus the detail panel when it opens (keyboard users land in it).
  useEffect(() => {
    if (openId && panelRef.current) panelRef.current.focus();
  }, [openId]);

  const submit = useCallback(() => {
    setSubmitted(true);
    onActivityResponse?.({
      activityType: 'hotspot-explorer',
      mode: 'survey',
      responseData: {
        explored,
        explorationOrder: [...explored],
        allExplored,
        reflection: reflection.trim(),
      },
    });
  }, [explored, allExplored, reflection, onActivityResponse]);

  const openHotspotData = openId ? data.hotspots.find((h) => h.id === openId) ?? null : null;

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[860px] px-6 py-10">
        <header className="text-center mb-6">
          <h1
            style={{
              ...slideTypographyStyle(theme, 'heading'),
              color: theme.colors.text,
              marginBottom: 8,
            }}
          >
            {data.title}
          </h1>
          {data.subtitle && (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>
          )}
        </header>

        <div className="text-xs mb-3 text-center" style={{ color: theme.colors.textMuted }}>
          Explored: {explored.length} of {data.hotspots.length}
        </div>

        {/* Image with overlaid numbered markers. Container is described to
            screen readers via aria-label so non-visual users get the orient. */}
        <div
          role="region"
          aria-label={`Interactive diagram: ${data.imageAlt}. ${data.hotspots.length} hotspots to explore.`}
          className="relative w-full mb-6 rounded overflow-hidden"
          style={{ border: `1px solid ${theme.colors.textMuted}33` }}
        >
          {data.imageUrl ? (
            <img
              src={data.imageUrl}
              alt={data.imageAlt}
              className="block w-full h-auto"
              style={{ display: 'block' }}
            />
          ) : (
            <div
              className="w-full flex items-center justify-center text-sm py-20"
              style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}
            >
              No image set
            </div>
          )}
          {data.hotspots.map((h, i) => {
            const isExplored = explored.includes(h.id);
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => openHotspot(h.id)}
                aria-label={`Hotspot ${i + 1}: ${h.label}${isExplored ? ' (explored)' : ''}`}
                title={h.label}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center font-bold text-sm shadow-md transition-transform hover:scale-110 focus:outline-none focus:ring-2"
                style={{
                  left: `${h.x}%`,
                  top: `${h.y}%`,
                  width: 32,
                  height: 32,
                  background: isExplored ? `${accent}cc` : accent,
                  color: '#fff',
                  border: `2px solid ${isExplored ? '#ffffffaa' : '#fff'}`,
                  outline: 'none',
                  // Focus ring matches the accent for non-colour identification.
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                }}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        {data.imageCaption && (
          <p
            className="text-xs italic text-center mb-6"
            style={{ color: theme.colors.textMuted }}
          >
            {data.imageCaption}
          </p>
        )}

        {/* Hotspot list — text-only fallback for keyboard-first users. Same
            buttons as the image overlays, presented as a vertical menu. */}
        <details className="mb-6 text-sm" style={{ color: theme.colors.textMuted }}>
          <summary className="cursor-pointer select-none">List view (keyboard-friendly)</summary>
          <ul className="mt-2 space-y-1 pl-4">
            {data.hotspots.map((h, i) => {
              const isExplored = explored.includes(h.id);
              return (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => openHotspot(h.id)}
                    className="text-left underline"
                    style={{ color: theme.colors.text }}
                  >
                    {i + 1}. {h.label}
                    {isExplored ? ' ✓' : ''}
                  </button>
                </li>
              );
            })}
          </ul>
        </details>

        {/* Detail panel — appears below the image when a hotspot is open.
            Inline (not a modal) so it works inside the scroll surface. */}
        <AnimatePresence>
          {openHotspotData ? (
            <Reveal variant="fade" spring="gentle">
              <div
                ref={panelRef}
                tabIndex={-1}
                role="dialog"
                aria-label={`Detail: ${openHotspotData.title}`}
                className="border rounded p-4 mb-6 focus:outline-none"
                style={{
                  borderColor: accent,
                  background: theme.colors.background,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 style={{ ...slideTypographyStyle(theme, 'subheading'), color: theme.colors.text, marginBottom: 0 }}>
                    {openHotspotData.title}
                  </h3>
                  <button
                    type="button"
                    onClick={closePanel}
                    aria-label="Close hotspot detail (Esc)"
                    className="text-sm ml-3"
                    style={{ color: theme.colors.textMuted }}
                  >
                    Close
                  </button>
                </div>
                <p
                  className="text-sm leading-relaxed whitespace-pre-line"
                  style={{ color: theme.colors.text }}
                >
                  {openHotspotData.body}
                </p>
              </div>
            </Reveal>
          ) : null}
        </AnimatePresence>

        {/* Synthesis block — only renders once every hotspot has been opened. */}
        <AnimatePresence mode="wait">
          {allExplored ? (
            <Reveal
              key="synthesis"
              variant="scale"
              spring="bouncy"
              className="rounded-2xl p-6 shadow-sm border-l-[5px]"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              <h2
                style={{
                  ...slideTypographyStyle(theme, 'subheading'),
                  color: theme.colors.text,
                  marginBottom: 8,
                }}
              >
                {data.synthesis.title}
              </h2>
              <p className="text-sm leading-relaxed mb-4" style={{ color: theme.colors.text }}>
                {data.synthesis.body}
              </p>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: theme.colors.textMuted }}
              >
                Your reflection
              </label>
              <p className="text-sm mb-2" style={{ color: theme.colors.text }}>
                {data.synthesis.reflectionPrompt}
              </p>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                rows={3}
                disabled={submitted}
                placeholder="Write a sentence or two…"
                className="w-full px-3 py-2 rounded border text-sm mb-3"
                style={{
                  background: theme.colors.background,
                  borderColor: `${theme.colors.textMuted}33`,
                  color: theme.colors.text,
                }}
              />
              <button
                type="button"
                onClick={submit}
                disabled={submitted || reflection.trim().length === 0}
                className="px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
                style={{ background: accent, color: '#fff' }}
              >
                {submitted ? 'Submitted' : 'Submit reflection'}
              </button>
            </Reveal>
          ) : (
            <p key="locked" className="text-xs italic text-center" style={{ color: theme.colors.textMuted }}>
              Explore every hotspot to unlock the synthesis.
            </p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---- Editor (minimal) -----------------------------------------------------

function HotspotEditor({
  data,
  theme,
  onUpdate,
}: {
  data: HotspotExplorerSlideData;
  theme: Theme;
  onUpdate: (data: HotspotExplorerSlideData) => void;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const inputStyle = {
    background: theme.colors.background,
    borderColor: `${theme.colors.textMuted}33`,
    color: theme.colors.text,
  } as const;

  const addHotspot = () => {
    onUpdate({
      ...data,
      hotspots: [
        ...data.hotspots,
        {
          id: crypto.randomUUID(),
          x: 50,
          y: 50,
          label: '',
          title: '',
          body: '',
        },
      ],
    });
  };

  const updateHotspot = (id: string, patch: Partial<HotspotExplorerSlideData['hotspots'][number]>) => {
    onUpdate({
      ...data,
      hotspots: data.hotspots.map((h) => (h.id === id ? { ...h, ...patch } : h)),
    });
  };

  const deleteHotspot = (id: string) => {
    onUpdate({ ...data, hotspots: data.hotspots.filter((h) => h.id !== id) });
  };

  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[760px] space-y-4">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />

        <div>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
            Image
          </label>
          <input
            value={data.imageUrl}
            onChange={(e) => onUpdate({ ...data, imageUrl: e.target.value })}
            placeholder="https://… (image URL)"
            className="w-full px-3 py-2 rounded border text-sm mt-1"
            style={inputStyle}
          />
          <input
            value={data.imageAlt}
            onChange={(e) => onUpdate({ ...data, imageAlt: e.target.value })}
            placeholder="Alt text (required for accessibility)"
            className="w-full px-3 py-2 rounded border text-sm mt-2"
            style={inputStyle}
          />
          <input
            value={data.imageCaption ?? ''}
            onChange={(e) => onUpdate({ ...data, imageCaption: e.target.value })}
            placeholder="Optional caption"
            className="w-full px-3 py-2 rounded border text-sm mt-2"
            style={inputStyle}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
              Hotspots ({data.hotspots.length})
            </label>
            <button
              type="button"
              onClick={addHotspot}
              className="text-xs px-2 py-1 rounded border"
              style={{ borderColor: accent, color: accent }}
            >
              + Add hotspot
            </button>
          </div>
          <div className="space-y-3">
            {data.hotspots.map((h, i) => (
              <div
                key={h.id}
                className="border rounded p-3"
                style={{ borderColor: `${theme.colors.textMuted}33` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
                    style={{ width: 22, height: 22, background: accent, color: '#fff' }}
                  >
                    {i + 1}
                  </span>
                  <input
                    value={h.label}
                    onChange={(e) => updateHotspot(h.id, { label: e.target.value })}
                    placeholder="Short label (e.g. 'Cooling tower')"
                    className="flex-1 px-2 py-1 rounded border text-sm"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => deleteHotspot(h.id)}
                    aria-label={`Delete hotspot ${i + 1}`}
                    className="text-xs"
                    style={{ color: theme.colors.textMuted }}
                  >
                    Delete
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <label className="text-xs" style={{ color: theme.colors.textMuted }}>
                    X (0–100)
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={h.x}
                      onChange={(e) => updateHotspot(h.id, { x: Number(e.target.value) })}
                      className="w-full px-2 py-1 rounded border text-sm mt-1"
                      style={inputStyle}
                    />
                  </label>
                  <label className="text-xs" style={{ color: theme.colors.textMuted }}>
                    Y (0–100)
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={h.y}
                      onChange={(e) => updateHotspot(h.id, { y: Number(e.target.value) })}
                      className="w-full px-2 py-1 rounded border text-sm mt-1"
                      style={inputStyle}
                    />
                  </label>
                </div>
                <input
                  value={h.title}
                  onChange={(e) => updateHotspot(h.id, { title: e.target.value })}
                  placeholder="Detail panel title"
                  className="w-full px-2 py-1 rounded border text-sm mb-2"
                  style={inputStyle}
                />
                <textarea
                  value={h.body}
                  onChange={(e) => updateHotspot(h.id, { body: e.target.value })}
                  placeholder="Detail body (1–3 short paragraphs)"
                  rows={3}
                  className="w-full px-2 py-1 rounded border text-sm"
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>
            Synthesis
          </label>
          <input
            value={data.synthesis.title}
            onChange={(e) => onUpdate({ ...data, synthesis: { ...data.synthesis, title: e.target.value } })}
            placeholder="Synthesis title"
            className="w-full px-3 py-2 rounded border text-sm mt-1"
            style={inputStyle}
          />
          <textarea
            value={data.synthesis.body}
            onChange={(e) => onUpdate({ ...data, synthesis: { ...data.synthesis, body: e.target.value } })}
            placeholder="Synthesis body — 2-3 sentences tying the hotspots together"
            rows={2}
            className="w-full px-3 py-2 rounded border text-sm mt-2"
            style={inputStyle}
          />
          <textarea
            value={data.synthesis.reflectionPrompt}
            onChange={(e) =>
              onUpdate({ ...data, synthesis: { ...data.synthesis, reflectionPrompt: e.target.value } })
            }
            placeholder="Reflection prompt"
            rows={2}
            className="w-full px-3 py-2 rounded border text-sm mt-2"
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
}
